import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import {
    getInvitationDetails,
    prepareInvitationAcceptance,
    finalizeInvitationAcceptance,
    getInvitationForFinalize,
} from '../../lib/supabase/users';
import {
    FiMail, FiBriefcase, FiUser, FiLock, FiCheckCircle,
    FiAlertCircle, FiArrowRight, FiShield, FiEye, FiEyeOff,
    FiRefreshCw, FiArrowLeft
} from 'react-icons/fi';

// ─── OTP Code Input ───────────────────────────────────────────────────────────
const OtpInput = ({ onComplete, onReset }) => {
    const [digits, setDigits] = useState(['', '', '', '', '', '']);
    const refs = useRef([]);

    const update = (idx, val) => {
        if (!/^\d*$/.test(val)) return;
        const next = [...digits];
        next[idx] = val.slice(-1);
        setDigits(next);

        if (val && idx < 5) refs.current[idx + 1]?.focus();

        const full = next.join('');
        if (full.length === 6) onComplete(full);
    };

    const handleKeyDown = (idx, e) => {
        if (e.key === 'Backspace') {
            if (digits[idx]) {
                const next = [...digits];
                next[idx] = '';
                setDigits(next);
            } else if (idx > 0) {
                refs.current[idx - 1]?.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pasted.length === 6) {
            setDigits(pasted.split(''));
            refs.current[5]?.focus();
            onComplete(pasted);
        }
    };

    useEffect(() => {
        onReset(() => setDigits(['', '', '', '', '', '']));
    }, [onReset]);

    return (
        <div className="flex items-center justify-center gap-3">
            {digits.map((d, i) => (
                <input
                    key={i}
                    ref={el => refs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => update(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    className="w-12 h-14 text-center text-xl font-bold bg-gray-50 border-0 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all select-none"
                />
            ))}
        </div>
    );
};

// ─── Resend countdown ─────────────────────────────────────────────────────────
const ResendTimer = ({ onResend, isResending }) => {
    const [seconds, setSeconds] = useState(60);

    useEffect(() => {
        if (seconds <= 0) return;
        const t = setTimeout(() => setSeconds(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [seconds]);

    const handleResend = async () => {
        await onResend();
        setSeconds(60);
    };

    return seconds > 0 ? (
        <p className="text-center text-sm text-gray-400">
            Resend code in <span className="font-semibold text-gray-600">{seconds}s</span>
        </p>
    ) : (
        <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className="flex items-center justify-center gap-1.5 w-full text-sm text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50"
        >
            <FiRefreshCw className={`w-3.5 h-3.5 ${isResending ? 'animate-spin' : ''}`} />
            {isResending ? 'Sending…' : 'Resend code'}
        </button>
    );
};

const AcceptInvitation = () => {
    const { invitationId } = useParams();
    const navigate = useNavigate();

    const [invitation, setInvitation] = useState(null);
    const [view, setView] = useState('form'); // form | verify

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);

    const [error, setError] = useState('');
    const [verifyError, setVerifyError] = useState('');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const otpResetRef = useRef(null);

    const handleOtpReset = useCallback((fn) => {
        otpResetRef.current = fn;
    }, []);

    useEffect(() => {
        fetchInvitation();
    }, [invitationId]);

    const fetchInvitation = async () => {
        try {
            setLoading(true);
            const data = await getInvitationDetails(invitationId);
            setInvitation(data);
        } catch (error) {
            console.error('Error fetching invitation:', error);
            setError(error.message || 'Invalid or expired invitation');
        } finally {
            setLoading(false);
        }
    };

    const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // AuthContext's `loading` flag only reflects the very first page load —
    // it never flips back to true for a sign-in that happens later in the
    // same session, like the ones this page performs right after finalizing
    // an invitation (signInWithPassword for an already-existing account, or
    // the session signUp/verifyOtp hands back for a brand-new one).
    // Navigating to `/` immediately after such a sign-in would hit
    // RootRedirect before AuthContext's own async profile fetch (kicked off
    // by its SIGNED_IN listener) has resolved, and RootRedirect would bounce
    // to /login using a still-null userProfile. Login.js's own registration
    // flow works around this exact same gap with this same
    // poll-then-hard-redirect approach instead of trusting RootRedirect's
    // timing, so this mirrors that rather than touching the shared
    // AuthContext (used everywhere else in the app).
    const waitForCompanyDashboardAndRedirect = async (userId) => {
        for (let attempt = 0; attempt < 30; attempt++) {
            await sleep(500);

            const { data } = await supabase
                .from('users')
                .select('companies (slug)')
                .eq('id', userId)
                .maybeSingle();

            const slug = data?.companies?.slug;

            if (slug) {
                window.location.replace(`/${slug}/dashboard`);
                return true;
            }
        }

        return false;
    };

    // const handleSubmit = async (e) => {
    //     e.preventDefault();
    //     setError('');

    //     if (password.length < 8) {
    //         setError('Password must be at least 8 characters long');
    //         return;
    //     }

    //     if (password !== confirmPassword) {
    //         setError('Passwords do not match');
    //         return;
    //     }

    //     try {
    //         setSubmitting(true);

    //         const result = await prepareInvitationAcceptance(invitationId, password);
    //         return navigate('/login')

    //         // If confirmation is disabled and session exists, finalize immediately
    //         if (result?.session) {
    //             await finalizeInvitationAcceptance(invitationId);
    //             alert('Account created successfully! Please log in.');
    //             navigate('/login');
    //             return;
    //         }

    //         // Confirmation required
    //         setView('verify');
    //     } catch (error) {
    //         console.error('Error preparing invitation acceptance:', error);
    //         setError(error.message || 'Failed to create account');
    //     } finally {
    //         setSubmitting(false);
    //     }
    // };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        try {
            setSubmitting(true);

            const result = await prepareInvitationAcceptance(invitationId, password);

            const authUser = result?.user;

            // Supabase's signUp() masks its response for an email that already
            // belongs to a *confirmed* account — identities comes back [] (vs. one
            // entry for a genuine new signup), email_confirmed_at/confirmed_at come
            // back null, and no email is sent at all. That masking is exactly why
            // relying on email_confirmed_at/confirmed_at alone (below) sent people
            // to a "check your email" screen for a code that would never arrive —
            // an empty identities array is the one reliable signal Supabase gives
            // for "this account already exists," so it has to be checked first.
            const accountAlreadyExists = Array.isArray(authUser?.identities) && authUser.identities.length === 0;

            if (accountAlreadyExists) {
                // The password typed above was never applied to that pre-existing
                // account (signUp() no-ops for an existing user) — try it as a
                // sign-in on the chance this really is the same person re-entering
                // their real password, which finalizes the invitation immediately
                // with no OTP step needed at all.
                const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                    email: invitation.email,
                    password,
                });

                if (!signInError && signInData?.session) {
                    // Not swallowed: this is the one call that actually creates the
                    // public.users row for a pre-existing auth account (the DB
                    // trigger that normally does that only fires on a genuine
                    // auth.users INSERT, which signUp() never produced here since
                    // the account already existed). If it fails, navigating to `/`
                    // anyway would land on an authenticated session with no
                    // matching profile — RootRedirect would silently bounce that
                    // to /login with no explanation, so surface the real error
                    // instead via the outer catch below.
                    await finalizeInvitationAcceptance(invitationId);

                    const redirected = await waitForCompanyDashboardAndRedirect(signInData.session.user.id);
                    if (!redirected) {
                        setError('Account linked, but workspace is not ready yet. Please refresh the page.');
                    }
                    return;
                }

                setError('An account with this email already exists. Please log in with your existing password to accept this invitation.');
                return;
            }

            const isConfirmed =
                !!authUser?.email_confirmed_at ||
                !!authUser?.confirmed_at ||
                !!result?.session;

            if (isConfirmed) {
                // Not swallowed — see the matching comment in the already-exists
                // branch above for why a failure here must not be treated as
                // harmless.
                await finalizeInvitationAcceptance(invitationId);

                if (result?.session) {
                    // signUp already returned a live session (e.g. email confirmations
                    // are disabled project-wide) — no need to make them log in again
                    // with the password they just set.
                    const redirected = await waitForCompanyDashboardAndRedirect(result.session.user.id);
                    if (!redirected) {
                        setError('Account created, but workspace is not ready yet. Please refresh the page.');
                    }
                    return;
                }

                // isConfirmed was true from email_confirmed_at/confirmed_at alone,
                // with no session actually issued (no known trigger for this today,
                // but not guaranteed impossible) — fall back to asking them to log in.
                alert('Account created successfully! Please log in.');
                navigate('/login', {
                    replace: true,
                    state: {
                        email: invitation.email,
                        message: 'Account created successfully. Please log in.'
                    }
                });
                return;
            }

            setView('verify');
        } catch (error) {
            console.error('Error preparing invitation acceptance:', error);
            setError(error.message || 'Failed to create account');
        } finally {
            setSubmitting(false);
        }
    };

    const handleVerify = async (code) => {
        if (code.length !== 6) return;

        setVerifyError('');
        setVerifyLoading(true);

        try {
            const { data, error } = await supabase.auth.verifyOtp({
                email: invitation.email,
                token: code,
                type: 'signup',
            });

            if (error) {
                setVerifyError('Invalid or expired code. Please try again.');
                otpResetRef.current?.();
                setVerifyLoading(false);
                return;
            }

            if (data?.session) {
                const invitationRow = await getInvitationForFinalize(invitationId);

                // If trigger already handled acceptance, just continue
                if (!invitationRow?.accepted_at) {
                    await finalizeInvitationAcceptance(invitationId);
                }

                // verifyOtp already gave us a live session for this exact user — send
                // them straight into the app instead of making them log in again with
                // the password they just set.
                const redirected = await waitForCompanyDashboardAndRedirect(data.session.user.id);
                if (!redirected) {
                    setVerifyError('Account verified, but workspace is not ready yet. Please refresh the page.');
                }
                return;
            }

            setVerifyError('Verification succeeded, but no session was returned.');
        } catch (error) {
            console.error('Error verifying invitation signup:', error);
            setVerifyError(error.message || 'Verification failed. Please try again.');
        } finally {
            setVerifyLoading(false);
        }
    };

    const handleResend = async () => {
        setVerifyError('');
        setResendLoading(true);

        try {
            await supabase.auth.resend({
                type: 'signup',
                email: invitation.email,
            });
            otpResetRef.current?.();
        } catch (error) {
            console.error('Error resending code:', error);
            setVerifyError('Failed to resend code. Please try again.');
        } finally {
            setResendLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading invitation...</p>
                </div>
            </div>
        );
    }

    if (error && !invitation) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center border border-gray-100">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-50 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <FiAlertCircle className="w-10 h-10 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Invalid Invitation</h2>
                    <p className="text-gray-600 mb-8">{error}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl font-medium"
                    >
                        Go to Login
                        <FiArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="mobile-public-page invitation-page min-h-screen flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full border border-gray-100">
                <div className="text-center mb-6">
                    <img
                        src="/assets/wide-logo-black.svg"
                        alt="Notify"
                        className="h-12 mx-auto mb-8"
                    />
                </div>

                {view === 'form' && (
                    <>
                        <div className="text-center mb-8">
                            <div className="relative inline-block mb-6">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl blur-xl opacity-20"></div>

                                {invitation.companies?.logo_url ? (
                                    <div className="relative w-24 h-24 mx-auto bg-white rounded-2xl shadow-lg p-3 border-2 border-gray-100">
                                        <img
                                            src={invitation.companies.logo_url}
                                            alt={invitation.companies.name}
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                ) : (
                                    <div className="relative w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center shadow-lg border-2 border-gray-100">
                                        <FiBriefcase className="w-12 h-12 text-blue-600" />
                                    </div>
                                )}
                            </div>

                            <h1 className="text-3xl font-bold text-gray-900 mb-3 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                You're Invited!
                            </h1>
                            <p className="text-gray-600 text-lg">
                                Join <strong className="text-gray-900">{invitation.companies?.name}</strong> as a{' '}
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-full text-sm font-semibold text-blue-700 capitalize">
                                    <FiShield className="w-3 h-3" />
                                    {invitation.role}
                                </span>
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-5 mb-8 space-y-3 border border-gray-200">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                    <FiMail className="w-5 h-5 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Email</p>
                                    <p className="font-semibold text-gray-900 truncate">{invitation.email}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                                    <FiUser className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Username</p>
                                    <p className="font-semibold text-gray-900 truncate">{invitation?.full_name || invitation.username}</p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                                    <FiLock className="w-4 h-4 text-blue-600" />
                                    Create Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none bg-white"
                                        placeholder="Enter your password"
                                        required
                                        minLength={8}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                                    <FiLock className="w-4 h-4 text-indigo-600" />
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none bg-white"
                                        placeholder="Confirm your password"
                                        required
                                        minLength={8}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        {showConfirmPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
                                    <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-800 font-medium">{error}</p>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={submitting}
                                className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2 group"
                            >
                                {submitting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Creating Account...
                                    </>
                                ) : (
                                    <>
                                        <FiCheckCircle className="w-5 h-5" />
                                        Continue
                                        <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                    </>
                                )}
                            </button>
                        </form>
                    </>
                )}

                {view === 'verify' && (
                    <div className="space-y-8">
                        <button
                            type="button"
                            onClick={() => {
                                setView('form');
                                setVerifyError('');
                            }}
                            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <FiArrowLeft className="w-4 h-4" />
                            Back
                        </button>

                        <div className="text-center space-y-3">
                            <div className="flex justify-center">
                                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
                                    <FiMail className="w-8 h-8 text-blue-500" />
                                </div>
                            </div>

                            <h1 className="text-3xl font-semibold text-gray-700">Check your email</h1>
                            <p className="text-gray-500 text-sm leading-relaxed">
                                We sent a 6-digit code to<br />
                                <span className="font-semibold text-gray-700">{invitation.email}</span>
                            </p>
                        </div>

                        {verifyError && (
                            <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                                <p className="text-sm text-red-600 text-center">{verifyError}</p>
                            </div>
                        )}

                        <div className="space-y-6">
                            {verifyLoading ? (
                                <div className="flex flex-col items-center gap-3 py-4">
                                    <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    <p className="text-sm text-gray-500">Verifying your account…</p>
                                </div>
                            ) : (
                                <OtpInput onComplete={handleVerify} onReset={handleOtpReset} />
                            )}

                            <ResendTimer onResend={handleResend} isResending={resendLoading} />

                            <p className="text-center text-xs text-gray-400 leading-relaxed">
                                Didn’t receive it? Check your spam folder.<br />
                                The code expires in 60 minutes.
                            </p>
                        </div>
                    </div>
                )}

                <div className="mt-8 pt-6 border-t border-gray-200">
                    <p className="text-xs text-center text-gray-500 flex items-center justify-center gap-2">
                        <FiShield className="w-3 h-3" />
                        By accepting this invitation, you agree to the terms of service
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvitation;